var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { createElement } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { first, get, isNotNullOrEmpty, removeKey } from '@pcty/utils';
import { useTheme } from '@citrus/user';
/**
 * Generates styled react component
 *
 * @deprecated Use citrusStyled instead
 * @param {string} name - Name of the generated component
 * @param {object} styles - Javascript style object that gets applied to component
 * @param {string} element - Type of dom element that component uses
 * @param {object} [config] - Object shaped like {classNames = [], combinators = {}}. 'classNames' are additional classes that get assigned to the component. 'combinators' are additional styles that get applied as a separate class to the component.
 * @returns {component}
 */
export var makeComponent = function (name, styles, element, config) {
    return React.forwardRef(function (_a, ref) {
        var _b = _a.css, css = _b === void 0 ? {} : _b, _c = _a.className, className = _c === void 0 ? '' : _c, _d = _a.as, as = _d === void 0 ? element : _d, children = _a.children, _e = _a.styleNames, styleNames = _e === void 0 ? [] : _e, other = __rest(_a, ["css", "className", "as", "children", "styleNames"]);
        var _f = [get(config, 'classNames'), get(config, 'combinators')], _g = _f[0], classNames = _g === void 0 ? [] : _g, _h = _f[1], combinators = _h === void 0 ? {} : _h;
        return createElement(as, __assign(__assign(__assign({ className: [
                cx(emotionCss(styles), emotionCss(css), __spreadArray(__spreadArray([], classNames, true), [className], false).join(' ').trim()),
                isNotNullOrEmpty(combinators) ? emotionCss(combinators) : '',
            ]
                .join(' ')
                .trim() }, removeKey(other, 'debug')), getDebugStyled(sessionStorage.getItem('citrus-debug') === 'true', __spreadArray([name], styleNames, true))), { ref: ref }), children);
    });
};
/** @private */
var citrusStyledFactory = function (element) {
    return function (name, styles, config) {
        return React.forwardRef(function (_a, ref) {
            var _b = _a.css, css = _b === void 0 ? {} : _b, _c = _a.className, className = _c === void 0 ? '' : _c, _d = _a.as, as = _d === void 0 ? element : _d, children = _a.children, _e = _a.styleNames, styleNames = _e === void 0 ? [] : _e, other = __rest(_a, ["css", "className", "as", "children", "styleNames"]);
            var theme = useTheme();
            var _styles = styles;
            if (typeof styles === 'object' && isNotNullOrEmpty(styles) && theme.name in styles) {
                // If the dark mode is on and dark mode styles are provided, use those, otherwise use standard theme styles
                _styles = (theme.darkModeDetection && theme.darkMode && styles["".concat(theme.name, "Dark")]) || styles[theme.name];
            }
            var _f = [get(config, 'classNames'), get(config, 'combinators')], _g = _f[0], classNames = _g === void 0 ? [] : _g, _h = _f[1], combinators = _h === void 0 ? {} : _h;
            var _combinators = combinators;
            if (isNotNullOrEmpty(combinators) && isNotNullOrEmpty(combinators[theme.name])) {
                _combinators = combinators[theme.name];
            }
            else if (isNotNullOrEmpty(combinators) && isNotNullOrEmpty(combinators.legacy)) {
                _combinators = combinators.legacy;
            }
            return createElement(as, __assign(__assign(__assign({ className: [
                    cx(emotionCss(_styles), emotionCss(css), __spreadArray(__spreadArray([], classNames, true), [className], false).join(' ').trim()),
                    isNotNullOrEmpty(_combinators) ? emotionCss(_combinators) : '',
                ]
                    .join(' ')
                    .trim() }, removeKey(other, 'debug')), getDebugStyled(sessionStorage.getItem('citrus-debug') === 'true', __spreadArray([name], styleNames, true))), { ref: ref }), children);
        });
    };
};
/**
 * Generates styled react component
 *
 * @param {any} element Type of dom element that component uses
 * @returns {(name: string, styles: any, config?: ConfigType) => React.FC<IStyledComponent>}
 */
export var citrusStyled = {
    as: citrusStyledFactory,
    a: citrusStyledFactory('a'),
    abbr: citrusStyledFactory('abbr'),
    address: citrusStyledFactory('address'),
    area: citrusStyledFactory('area'),
    article: citrusStyledFactory('article'),
    aside: citrusStyledFactory('aside'),
    audio: citrusStyledFactory('audio'),
    b: citrusStyledFactory('b'),
    base: citrusStyledFactory('base'),
    bdi: citrusStyledFactory('bdi'),
    bdo: citrusStyledFactory('bdo'),
    big: citrusStyledFactory('big'),
    blockquote: citrusStyledFactory('blockquote'),
    body: citrusStyledFactory('body'),
    br: citrusStyledFactory('br'),
    button: citrusStyledFactory('button'),
    canvas: citrusStyledFactory('canvas'),
    caption: citrusStyledFactory('caption'),
    cite: citrusStyledFactory('cite'),
    code: citrusStyledFactory('code'),
    col: citrusStyledFactory('col'),
    colgroup: citrusStyledFactory('colgroup'),
    data: citrusStyledFactory('data'),
    datalist: citrusStyledFactory('datalist'),
    dd: citrusStyledFactory('dd'),
    del: citrusStyledFactory('del'),
    details: citrusStyledFactory('details'),
    dfn: citrusStyledFactory('dfn'),
    dialog: citrusStyledFactory('dialog'),
    div: citrusStyledFactory('div'),
    dl: citrusStyledFactory('dl'),
    dt: citrusStyledFactory('dt'),
    em: citrusStyledFactory('em'),
    embed: citrusStyledFactory('embed'),
    fieldset: citrusStyledFactory('fieldset'),
    figcaption: citrusStyledFactory('figcaption'),
    figure: citrusStyledFactory('figure'),
    footer: citrusStyledFactory('footer'),
    form: citrusStyledFactory('form'),
    h1: citrusStyledFactory('h1'),
    h2: citrusStyledFactory('h2'),
    h3: citrusStyledFactory('h3'),
    h4: citrusStyledFactory('h4'),
    h5: citrusStyledFactory('h5'),
    h6: citrusStyledFactory('h6'),
    head: citrusStyledFactory('head'),
    header: citrusStyledFactory('header'),
    hgroup: citrusStyledFactory('hgroup'),
    hr: citrusStyledFactory('hr'),
    html: citrusStyledFactory('html'),
    i: citrusStyledFactory('i'),
    iframe: citrusStyledFactory('iframe'),
    img: citrusStyledFactory('img'),
    input: citrusStyledFactory('input'),
    ins: citrusStyledFactory('ins'),
    kbd: citrusStyledFactory('kbd'),
    keygen: citrusStyledFactory('keygen'),
    label: citrusStyledFactory('label'),
    legend: citrusStyledFactory('legend'),
    li: citrusStyledFactory('li'),
    link: citrusStyledFactory('link'),
    main: citrusStyledFactory('main'),
    map: citrusStyledFactory('map'),
    mark: citrusStyledFactory('mark'),
    marquee: citrusStyledFactory('marquee'),
    menu: citrusStyledFactory('menu'),
    menuitem: citrusStyledFactory('menuitem'),
    meta: citrusStyledFactory('meta'),
    meter: citrusStyledFactory('meter'),
    nav: citrusStyledFactory('nav'),
    noscript: citrusStyledFactory('noscript'),
    object: citrusStyledFactory('object'),
    ol: citrusStyledFactory('ol'),
    optgroup: citrusStyledFactory('optgroup'),
    option: citrusStyledFactory('option'),
    output: citrusStyledFactory('output'),
    p: citrusStyledFactory('p'),
    param: citrusStyledFactory('param'),
    picture: citrusStyledFactory('picture'),
    pre: citrusStyledFactory('pre'),
    progress: citrusStyledFactory('progress'),
    q: citrusStyledFactory('q'),
    rp: citrusStyledFactory('rp'),
    rt: citrusStyledFactory('rt'),
    ruby: citrusStyledFactory('ruby'),
    s: citrusStyledFactory('s'),
    samp: citrusStyledFactory('samp'),
    script: citrusStyledFactory('script'),
    section: citrusStyledFactory('section'),
    select: citrusStyledFactory('select'),
    small: citrusStyledFactory('small'),
    source: citrusStyledFactory('source'),
    span: citrusStyledFactory('span'),
    strong: citrusStyledFactory('strong'),
    style: citrusStyledFactory('style'),
    sub: citrusStyledFactory('sub'),
    summary: citrusStyledFactory('summary'),
    sup: citrusStyledFactory('sup'),
    table: citrusStyledFactory('table'),
    tbody: citrusStyledFactory('tbody'),
    td: citrusStyledFactory('td'),
    textarea: citrusStyledFactory('textarea'),
    tfoot: citrusStyledFactory('tfoot'),
    th: citrusStyledFactory('th'),
    thead: citrusStyledFactory('thead'),
    time: citrusStyledFactory('time'),
    title: citrusStyledFactory('title'),
    tr: citrusStyledFactory('tr'),
    track: citrusStyledFactory('track'),
    u: citrusStyledFactory('u'),
    ul: citrusStyledFactory('ul'),
    var: citrusStyledFactory('var'),
    video: citrusStyledFactory('video'),
    wbr: citrusStyledFactory('wbr'),
    circle: citrusStyledFactory('circle'),
    clipPath: citrusStyledFactory('clipPath'),
    defs: citrusStyledFactory('defs'),
    ellipse: citrusStyledFactory('ellipse'),
    foreignObject: citrusStyledFactory('foreignObject'),
    g: citrusStyledFactory('g'),
    image: citrusStyledFactory('image'),
    line: citrusStyledFactory('line'),
    linearGradient: citrusStyledFactory('linearGradient'),
    mask: citrusStyledFactory('mask'),
    path: citrusStyledFactory('path'),
    pattern: citrusStyledFactory('pattern'),
    polygon: citrusStyledFactory('polygon'),
    polyline: citrusStyledFactory('polyline'),
    radialGradient: citrusStyledFactory('radialGradient'),
    rect: citrusStyledFactory('rect'),
    stop: citrusStyledFactory('stop'),
    svg: citrusStyledFactory('svg'),
    text: citrusStyledFactory('text'),
    tspan: citrusStyledFactory('tspan'),
};
/**
 * Generates styled react component
 *
 * @param {Record<string, boolean>} obj Object with key/value aligned with the style name and whether or not it's being used
 * @returns {string[]}
 */
export var getStyleNames = function (obj) {
    var keys = Object.keys(obj);
    var styleNames = [];
    for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
        var key = keys_1[_i];
        if (obj[key]) {
            styleNames = __spreadArray(__spreadArray([], styleNames, true), [key], false);
        }
    }
    return styleNames;
};
/**
 * Generates automation IDs for elements
 *
 * @param {string} id - Base ID to be assigned
 * @param {string} [suffix] - Suffixed appended to base ID if present
 * @returns {object}
 */
export var getAutomation = function (automationId, suffix) {
    return !automationId ? {} : { 'data-automation-id': automationId + (suffix ? "-".concat(suffix) : '') };
};
/**
 * Gets debug information for a component
 *
 * @param {bool} debugging - Whether or not we're debugging
 * @param {string} componentId - ID of component to debug
 * @param {string} version - Version of component to pass through in debug info
 * @returns {object}
 */
export var getDebug = function (debugging, componentId, version) {
    return debugging ? { 'data-component-id': componentId, 'data-version': version } : {};
};
/**
 * Gets debug information for a styled component
 *
 * @param {string} debugging - Whether or not we're debugging
 * @param {string} styledComponent - Name of styled component to debug
 * @returns {object}
 */
export var getDebugStyled = function (debugging, styledComponent) {
    return debugging ? { 'data-style': styledComponent.join(', ') } : {};
};
/**
 * Gets the string type of the component or core html (JSX) element. React Fragments will return type 'react.fragment'. Priority will be given to the prop '__TYPE'.
 *
 * @deprecated since v3.14.0 - use 'react-nanny' instead
 * @since v3.5.0
 * @param {T} component - The component to type check
 * @returns {string} - The string representation of the type
 */
export var typeOfComponent = function (component) {
    return get(component, 'props.__TYPE') || get(component, 'type', '').toString().replace('Symbol(react.fragment)', 'react.fragment') || undefined;
};
/**
 * Gets first child by specified type. This function will check the prop '__TYPE' first and then the 'type' string to match core html elements. To find a React Fragment, search for type 'react.fragment'.
 *
 * @deprecated since v3.14.0 - use 'react-nanny' instead
 * @param {ReactNode} children - JSX children
 * @param {string[]} types - Types of children to match
 * @returns {T} - The first matching child
 * @example
 * // Finds the first occurrence of either a Banner, a div, or a React Fragment
 * getChildByType(children, ['Banner', 'div', 'react.fragment']);
 */
export var getChildByType = function (children, types) {
    return first(React.Children.toArray(children).filter(function (child) { return types.indexOf(typeOfComponent(child)) !== -1; }), undefined);
};
/**
 * Gets all children by specified type. This function will check the prop '__TYPE' first and then the 'type' string to match core html elements. To find a React Fragment, search for type 'react.fragment'.
 *
 * @deprecated since v3.14.0 - use 'react-nanny' instead
 * @param {ReactNode} children - JSX children
 * @param {string[]} types - Types of children to match
 * @returns {T[]} - Array of matching children
 * @example
 * // Finds all occurrences of Banner, div, and React Fragment
 * getChildrenByType(children, ['Banner', 'div', 'react.fragment']);
 */
export var getChildrenByType = function (children, types) {
    return React.Children.toArray(children).filter(function (child) { return types.indexOf(typeOfComponent(child)) !== -1; });
};
/**
 * Removes all children by specified type. This function will check the prop '__TYPE' first and then the 'type' string to match core html elements. To remove a React Fragment, use type 'react.fragment'.
 *
 * @deprecated since v3.14.0 - use 'react-nanny' instead
 * @param {ReactNode} children - JSX children
 * @param {string[]} types - Types of children to match
 * @returns {T[]} - All non-matching children
 * @example
 * // Removes all occurrences of Banner, div, and React Fragment
 * removeChildrenByType(children, ['Banner', 'div', 'react.fragment']);
 */
export var removeChildrenByType = function (children, types) {
    return React.Children.toArray(children).filter(function (child) { return types.indexOf(typeOfComponent(child)) === -1; });
};
/**
 * Ensures component type is not overwritten
 * @deprecated since transition to TypeScript (v3.2.0)
 *
 * @param {string} expectedType - The expected type to validate against
 * @returns {func} Custom PropTypes validation function
 */
export var typeValidation = function (expectedType) { return function (props, propName, componentName) {
    if (props[propName] !== expectedType) {
        return new Error("'".concat(propName, "' in '").concat(componentName, "'\n\nYou may NOT pass in a prop value for '").concat(propName, "'. ") +
            'It had 2 underscores in the prop name for a reason. So, if you would kindly remove it, we can all go about our error free day.\n');
    }
}; };
/**
 * Gets localization (translation) for a given type/region
 *
 * @since v3.2.0
 * @param {Map<TType, TLocalization>} localizationMap All of the available localizations
 * @param {TType} localizeTo Localize to this type/region
 * @param {TLocalization} [custom] Customized localization
 * @returns {TLocalization} Description about return value
 */
export var getLocalization = function (localizationMap, localizeTo, custom) {
    localizeTo = isNotNullOrEmpty(localizeTo) ? localizeTo : localStorage.getItem('pcty-localization');
    var base = localizationMap.get(localizeTo) || localizationMap.get('en-US');
    return isNotNullOrEmpty(custom) ? __assign(__assign({}, base), custom) : localizationMap.get(localizeTo) || base;
};
